import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";
import "./PurchaseDetailPage.css";

type PurchaseItem = {
  id: string;
  description: string;
  quantity: number | string;
  received_quantity: number | string;
  remaining_quantity: number | string;
  unit_cost: number | string;
  line_total: number | string;
  catalog_item_id: string | null;
  catalog_name: string | null;
  catalog_sku: string | null;
};

type PurchaseOrder = {
  id: string;
  organization_id: string;
  purchase_number: string;
  status: string;
  order_date: string;
  expected_date: string | null;
  currency_code: string;
  total: number | string;
  warehouse_id: string | null;
};

type Supplier = {
  id: string;
  name: string;
  document_type: string | null;
  document_number: string | null;
  email: string | null;
  phone: string | null;
};

type Warehouse = {
  id: string;
  name: string;
};

type Receipt = {
  id: string;
  receipt_number: string;
  received_at: string;
  notes: string | null;
};

type AccountingPurchaseDocument = {
  id: string;
  document_type: string;
  series: string;
  document_number: string;
  issue_date: string;
  due_date: string | null;
  currency_code: string;
  total: number | string;
  status: string;
  sunat_status: string;
  payable_id: string | null;
  payable_status: string | null;
  payable_balance: number | string | null;
};

type Detail = {
  order: PurchaseOrder;
  supplier: Supplier | null;
  warehouse: Warehouse | null;
  items: PurchaseItem[];
  receipts: Receipt[];
  accounting_documents: AccountingPurchaseDocument[];
};

type WarehouseOption = {
  id: string;
  name: string;
  status?: string;
};

type ImportShipmentLink = {
  id: string;
  import_number: string;
  status: string;
  origin_country_code: string | null;
  destination_country_code: string | null;
  incoterm: string | null;
  shipment_date: string | null;
  estimated_arrival_date: string | null;
  arrival_date: string | null;
  updated_at: string;
};

function money(value: number | string | null, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function today() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
  }).format(new Date());
}

export function PurchaseDetailPage() {
  const { purchaseId } = useParams();
  const { selectedOrganization } = useOrganization();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [importShipments, setImportShipments] = useState<ImportShipmentLink[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [accountingBusy, setAccountingBusy] = useState(false);
  const [showAccountingForm, setShowAccountingForm] = useState(false);
  const [documentType, setDocumentType] = useState("invoice");
  const [documentSeries, setDocumentSeries] = useState("");
  const [documentNumber, setDocumentNumber] = useState("");
  const [documentIssueDate, setDocumentIssueDate] = useState(today());
  const [documentMode, setDocumentMode] = useState<"cash" | "credit">("cash");
  const [documentDueDate, setDocumentDueDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!purchaseId || !selectedOrganization) return;

    setLoading(true);
    setError(null);

    const [detailResult, warehousesResult, importResult] = await Promise.all([
      supabase.rpc("get_purchase_order_detail", {
        p_purchase_order_id: purchaseId,
      }),
      supabase.rpc("list_inventory_warehouses", {
        p_organization_id: selectedOrganization.organizationId,
      }),
      supabase.rpc("list_purchase_import_shipments", {
        p_purchase_order_id: purchaseId,
      }),
    ]);

    if (detailResult.error || warehousesResult.error || importResult.error) {
      setError(
        detailResult.error?.message ??
          warehousesResult.error?.message ??
          importResult.error?.message ??
          "No se pudo cargar la orden.",
      );
      setLoading(false);
      return;
    }

    const nextDetail = detailResult.data as Detail;
    setDetail(nextDetail);

    const nextWarehouses = (warehousesResult.data ?? []) as WarehouseOption[];
    setWarehouses(nextWarehouses);
    setImportShipments(
      importResult.error
        ? []
        : ((importResult.data ?? []) as ImportShipmentLink[]),
    );
    setWarehouseId(
      nextDetail.order.warehouse_id ??
        nextWarehouses.find((warehouse) => !warehouse.status || warehouse.status === "active")?.id ??
        "",
    );

    const nextQuantities: Record<string, string> = {};
    for (const item of nextDetail.items) {
      const remaining = Number(item.remaining_quantity || 0);
      nextQuantities[item.id] = remaining > 0 ? String(remaining) : "0";
    }
    setQuantities(nextQuantities);
    setLoading(false);
  }, [purchaseId, selectedOrganization]);

  useEffect(() => {
    void load();
  }, [load]);

  const canReceive = useMemo(
    () =>
      detail &&
      ["ordered", "partially_received"].includes(detail.order.status) &&
      detail.items.some((item) => Number(item.remaining_quantity) > 0),
    [detail],
  );

  async function registerSupplierDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!detail) return;

    if (!documentSeries.trim() || !documentNumber.trim()) {
      setError("Completa serie y número del comprobante.");
      return;
    }

    if (documentMode === "credit" && !documentDueDate) {
      setError("Define el vencimiento para la compra a crédito.");
      return;
    }

    setAccountingBusy(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc(
      "register_accounting_purchase_from_order_mode",
      {
        p_purchase_order_id: detail.order.id,
        p_document_type: documentType,
        p_series: documentSeries.trim(),
        p_document_number: documentNumber.trim(),
        p_issue_date: documentIssueDate,
        p_on_credit: documentMode === "credit",
        p_due_date: documentMode === "credit" ? documentDueDate : null,
      },
    );

    setAccountingBusy(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setDocumentSeries("");
    setDocumentNumber("");
    setDocumentMode("cash");
    setDocumentDueDate("");
    setShowAccountingForm(false);
    setSuccess(
      documentMode === "credit"
        ? "Comprobante registrado y CxP creada."
        : "Comprobante registrado y CxP creada con vencimiento en la fecha de emisión.",
    );
    await load();
  }

  async function receive() {
    if (!detail || !warehouseId) {
      setError("Selecciona un almacén.");
      return;
    }

    const items = detail.items
      .map((item) => ({
        purchase_order_item_id: item.id,
        quantity: Number(quantities[item.id] || 0),
        unit_cost: Number(item.unit_cost || 0),
      }))
      .filter((item) => item.quantity > 0);

    if (items.length === 0) {
      setError("Ingresa al menos una cantidad a recibir.");
      return;
    }

    for (const item of detail.items) {
      const quantity = Number(quantities[item.id] || 0);
      if (quantity < 0 || quantity > Number(item.remaining_quantity || 0)) {
        setError(`La cantidad de "${item.description}" supera el saldo pendiente.`);
        return;
      }
    }

    setReceiving(true);
    setError(null);
    setSuccess(null);

    const { error: rpcError } = await supabase.rpc("receive_purchase_order", {
      p_purchase_order_id: detail.order.id,
      p_warehouse_id: warehouseId,
      p_notes: null,
      p_items: items,
    });

    setReceiving(false);

    if (rpcError) {
      setError(rpcError.message);
      return;
    }

    setSuccess("Recepción registrada e inventario actualizado.");
    await load();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
        </section>
      </div>
    );
  }

  if (loading && !detail) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando orden de compra…</strong>
        </section>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="page">
        <div className="inline-alert">{error ?? "Orden no disponible."}</div>
        <Link className="compact-button" to="/compras">Volver a compras</Link>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Orden de compra</span>
          <h1>{detail.order.purchase_number}</h1>
          <p>
            {detail.supplier ? (
              <Link
                className="record-inline-link"
                to={`/proveedores/${detail.supplier.id}`}
              >
                {detail.supplier.name}
              </Link>
            ) : (
              "Proveedor"
            )}
          </p>
        </div>
        <Link className="ghost-button purchase-back-link" to="/compras">
          Volver
        </Link>
      </header>

      {error ? <div className="inline-alert">{error}</div> : null}
      {success ? <div className="form-success purchase-detail-message">{success}</div> : null}

      <section className="purchase-detail-summary">
        <div className="metric-card">
          <span>Estado</span>
          <strong className="purchase-detail-state">{detail.order.status}</strong>
        </div>
        <div className="metric-card">
          <span>Total</span>
          <strong>{money(detail.order.total, detail.order.currency_code)}</strong>
        </div>
        <div className="metric-card">
          <span>Recepciones</span>
          <strong>{detail.receipts.length}</strong>
        </div>
      </section>

      {importShipments.length > 0 ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Importación</span>
              <h2>Embarques vinculados</h2>
            </div>
            <span className="status-badge">{importShipments.length}</span>
          </div>

          <div className="purchase-receipts">
            {importShipments.map((shipment) => (
              <article key={shipment.id}>
                <div>
                  <strong>{shipment.import_number}</strong>
                  <span>
                    {[shipment.origin_country_code, shipment.incoterm]
                      .filter(Boolean)
                      .join(" · ") || "Sin datos logísticos"}
                  </span>
                  <small>
                    {shipment.arrival_date
                      ? `Llegada ${shipment.arrival_date}`
                      : shipment.estimated_arrival_date
                        ? `ETA ${shipment.estimated_arrival_date}`
                        : "Sin fecha de llegada"}
                  </small>
                </div>
                <div className="quotation-actions">
                  <span className="status-badge">{shipment.status}</span>
                  <Link
                    className="compact-button"
                    to={`/importaciones/${shipment.id}`}
                  >
                    Abrir importación
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="panel-card">
        <div className="section-heading purchase-accounting-heading">
          <div>
            <span className="eyebrow">Contabilidad</span>
            <h2>Comprobantes del proveedor</h2>
          </div>

          {["ordered", "partially_received", "received"].includes(
            detail.order.status,
          ) ? (
            <button
              className="ghost-button"
              type="button"
              onClick={() => setShowAccountingForm((value) => !value)}
            >
              Registrar comprobante
            </button>
          ) : null}
        </div>

        {showAccountingForm ? (
          <form
            className="purchase-accounting-form"
            onSubmit={registerSupplierDocument}
          >
            <label>
              Tipo
              <select
                value={documentType}
                onChange={(event) => setDocumentType(event.target.value)}
              >
                <option value="invoice">Factura</option>
                <option value="receipt">Boleta / recibo</option>
                <option value="other">Otro</option>
              </select>
            </label>

            <label>
              Serie
              <input
                required
                value={documentSeries}
                onChange={(event) => setDocumentSeries(event.target.value)}
              />
            </label>

            <label>
              Número
              <input
                required
                value={documentNumber}
                onChange={(event) => setDocumentNumber(event.target.value)}
              />
            </label>

            <label>
              Emisión
              <input
                required
                type="date"
                value={documentIssueDate}
                onChange={(event) => setDocumentIssueDate(event.target.value)}
              />
            </label>

            <label>
              Condición
              <select
                value={documentMode}
                onChange={(event) =>
                  setDocumentMode(event.target.value as "cash" | "credit")
                }
              >
                <option value="cash">Contado</option>
                <option value="credit">Crédito</option>
              </select>
            </label>

            {documentMode === "credit" ? (
              <label>
                Vencimiento
                <input
                  required
                  type="date"
                  min={documentIssueDate}
                  value={documentDueDate}
                  onChange={(event) => setDocumentDueDate(event.target.value)}
                />
              </label>
            ) : (
              <p className="settings-empty">
                Contado: la CxP vencerá el día de emisión y el pago se registrará
                desde Tesorería.
              </p>
            )}

            <div className="purchase-accounting-actions">
              <button
                className="primary-button"
                type="submit"
                disabled={accountingBusy}
              >
                {accountingBusy ? "Registrando…" : "Guardar comprobante"}
              </button>
            </div>
          </form>
        ) : null}

        {detail.accounting_documents.length === 0 ? (
          <p className="settings-empty">
            Aún no hay comprobantes vinculados a esta orden.
          </p>
        ) : (
          <div className="purchase-accounting-documents">
            {detail.accounting_documents.map((document) => (
              <article key={document.id}>
                <div>
                  <span className="status-badge">{document.status}</span>
                  <strong>
                    {document.series}-{document.document_number}
                  </strong>
                  <small>
                    {document.document_type} · {document.issue_date} · Fiscal{" "}
                    {document.sunat_status}
                  </small>
                </div>

                <div className="purchase-accounting-amount">
                  <strong>
                    {money(document.total, document.currency_code)}
                  </strong>
                  {document.payable_id ? (
                    <>
                      <small>
                        CxP {document.payable_status} · saldo{" "}
                        {money(document.payable_balance, document.currency_code)}
                      </small>
                      {Number(document.payable_balance || 0) > 0 ? (
                        <Link className="compact-button" to="/tesoreria">
                          Pagar en Tesorería
                        </Link>
                      ) : null}
                    </>
                  ) : (
                    <small>CxP pendiente de sincronizar</small>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Detalle</span>
            <h2>Productos solicitados</h2>
          </div>
        </div>

        <div className="purchase-detail-items">
          {detail.items.map((item) => (
            <article className="purchase-detail-item" key={item.id}>
              <div>
                <strong>{item.description}</strong>
                <span>
                  {item.catalog_sku ? `${item.catalog_sku} · ` : ""}
                  {item.catalog_name ?? "Ítem manual"}
                </span>
              </div>

              <dl>
                <div>
                  <dt>Pedido</dt>
                  <dd>{Number(item.quantity)}</dd>
                </div>
                <div>
                  <dt>Recibido</dt>
                  <dd>{Number(item.received_quantity)}</dd>
                </div>
                <div>
                  <dt>Pendiente</dt>
                  <dd>{Number(item.remaining_quantity)}</dd>
                </div>
              </dl>

              {canReceive && item.catalog_item_id ? (
                <label>
                  Recibir ahora
                  <input
                    type="number"
                    min="0"
                    max={Number(item.remaining_quantity)}
                    step="0.001"
                    value={quantities[item.id] ?? "0"}
                    onChange={(event) =>
                      setQuantities((current) => ({
                        ...current,
                        [item.id]: event.target.value,
                      }))
                    }
                  />
                </label>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      {canReceive ? (
        <section className="panel-card purchase-receive-panel">
          <div>
            <span className="eyebrow">Recepción</span>
            <h2>Ingresar al inventario</h2>
          </div>

          <label>
            Almacén
            <select value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">Seleccionar almacén</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </label>

          <button className="primary-button" type="button" disabled={receiving} onClick={() => void receive()}>
            {receiving ? "Registrando…" : "Registrar recepción"}
          </button>
        </section>
      ) : null}

      {detail.receipts.length > 0 ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Historial</span>
              <h2>Recepciones</h2>
            </div>
          </div>

          <div className="purchase-receipts">
            {detail.receipts.map((receipt) => (
              <article key={receipt.id}>
                <strong>{receipt.receipt_number}</strong>
                <span>{new Date(receipt.received_at).toLocaleString("es-PE")}</span>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
